import React, { useContext } from 'react'
import { Form, Typography } from 'antd'
import styled from 'styled-components'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import PatientContext from 'contexts/PatientContext'
import { useStore } from 'store'
import { CaregiverDateRangePicker } from 'components/Patients/CaregiverDatePicker'
import QuestionContainer from 'components/Assessments/components/QuestionContainer'
import {
  validateNotEmptyDateRangePicker,
  validateSameDayAppointmentOverviewSection
} from 'components/Assessments/helpers'
import { AssessmentOverviewFormField } from './formField'
import { AssessmentOverviewQuestionnaireItem } from './questionnaire'
import { PATSectionComponentProps } from '../../types/components'

const { Text } = Typography

export default function AssessmentOverviewSection({
  item: section,
  sectionId: _sectionId,
  sectionIndex: _sectionIndex
}: PATSectionComponentProps): JSX.Element {
  const { cantEditAssessmentFields, assessmentMustBeFinalized, response } = useAssessmentContext()
  const { idToPatientMapping } = useStore((store) => store.patient)
  const { patientId } = useContext(PatientContext)

  if (!patientId) {
    throw new Error('No patientId in PatientContext')
  }

  const caregiverTimezone = idToPatientMapping[patientId as string]?.caregiverTimezone

  const appointmentTimeField = section.item?.[0]

  if (!appointmentTimeField) {
    throw new Error('Could not find appointment time field within Questionnaire.')
  }

  const sectionDisabled = cantEditAssessmentFields
  const sectionOptional = !assessmentMustBeFinalized

  return (
    <>
      <QuestionContainer
        key={AssessmentOverviewQuestionnaireItem.ASSESSMENT_OVERVIEW_APPOINTMENT_TIME}
        linkId={AssessmentOverviewQuestionnaireItem.ASSESSMENT_OVERVIEW_APPOINTMENT_TIME}
      >
        <Container>
          <HalfWidthContainer>
            <Text strong>{appointmentTimeField.text}</Text>
          </HalfWidthContainer>
          <HalfWidthItemContainer
            name={AssessmentOverviewFormField.APPOINTMENT_TIME}
            rules={[
              {
                validator: async (_, value) => {
                  if (!sectionOptional) {
                    await validateNotEmptyDateRangePicker(value)
                  }
                  await validateSameDayAppointmentOverviewSection(value, caregiverTimezone)
                }
              }
            ]}
          >
            <CaregiverDateRangePicker
              disabled={sectionDisabled}
              timezone={caregiverTimezone}
              format="MM-DD-YYYY HH:mm z"
              showTime={{
                format: 'HH:mm'
              }}
            />
          </HalfWidthItemContainer>
        </Container>
        <Container>
          <HalfWidthContainer>
            <Text strong>Completed By: </Text>
          </HalfWidthContainer>
          <HalfWidthContainer>
            <Text>{response?.source?.display}</Text>
          </HalfWidthContainer>
        </Container>
      </QuestionContainer>
    </>
  )
}

const Container = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: row;
  align-items: baseline;

  > * {
    flex-basis: 100%;
  }
`

const HalfWidthContainer = styled.div`
  width: 50%;
  margin: 0 0 8px;
`

const HalfWidthItemContainer = styled(Form.Item)`
  width: 50%;
  margin: 0 0 8px;
`
